/* colorMapper.ts */

/* Chroma */
import chroma from "chroma-js";

/* Utils */
import { SANKEY_COLOR_MAPPING } from "src/utils/mapper/treatmentColorMapper";

/* Constants */
import { BASIC_COLORS } from "src/shared/constants/common/Colors";
import {
  NOT_LOOKED_FOR_LABEL,
  LOOKED_FOR_NO_VALUE_LABEL,
  isNoValueStateLabel,
} from "src/shared/constants/charts/NoValueStates";

// Grey-scale palette reserved for the no-value states, so they read as a
// distinct class from the coloured real cohort groups and are easy to spot.
// "Not looked for" (never assessed) is the darker grey; "Looked for, no value"
// (assessed, no result) the lighter one.
const NO_VALUE_STATE_COLORS: Record<string, string> = {
  [NOT_LOOKED_FOR_LABEL]: "#8a8f98",
  [LOOKED_FOR_NO_VALUE_LABEL]: "#c2c6cc",
};

const colorFromString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return chroma.hsl(hue, 0.65, 0.5).hex();
}

const randomColor = () => {
  const hue = Math.floor(Math.random() * 360);
  return chroma.hsl(hue, 0.65, 0.5).hex();
}

export const createGradient = (color: string) => {
  const startColor = "rgba(45, 64, 94, 1)";
  const endColor = chroma(color).alpha(1).css("rgb");
  return `linear-gradient(135deg, ${startColor} 25%, ${endColor} 100%)`;
};

export const getConsistentColor = (
  label: string
): string => {
  // Keep the no-value states ("Not looked for" / "Looked for, no value") on the
  // same reserved grey scale as descriptive statistics (see
  // `NO_VALUE_STATE_COLORS`), so they read as the same distinct class in the
  // sankey. Checked first so the grey always wins over any other mapping.
  if (label && NO_VALUE_STATE_COLORS[label]) {
    return NO_VALUE_STATE_COLORS[label];
  }
  if (label && SANKEY_COLOR_MAPPING[label]) {
    return SANKEY_COLOR_MAPPING[label];
  }
  if(!label || label.trim().length === 0) {
    return randomColor();
  }

  return colorFromString(label);
};

export const getColorByIndex = (index: number): string => {
  return chroma.scale(BASIC_COLORS).mode("hcl").colors(4)[
    index % 4
  ];
};

/**
 * Colour for a cohort group / stacked layer. The "Not looked for" and
 * "Looked for, no value" cohort groups get a reserved grey (see
 * `NO_VALUE_STATE_COLORS`) so they are visually distinct from real cohort
 * values; every other group falls back to the normal indexed palette. Used
 * consistently across the legend, pie chart, stacked bar chart, histogram and
 * box plot so a group's colour matches everywhere it appears.
 */
export const getCohortGroupColor = (
  name: string | null | undefined,
  index: number
): string => {
  if (name && NO_VALUE_STATE_COLORS[name]) {
    return NO_VALUE_STATE_COLORS[name];
  }
  return getColorByIndex(index);
};

/**
 * The gradient/colour key for one stacked-bar segment. With a single group
 * (no cohort breakdown), the injected "Not looked for"/"Looked for, no value"
 * *category* bars carry no composition, so they are keyed by their category
 * name and thus take the reserved grey (see `getCohortGroupColor`). With
 * multiple cohort groups those categories stay keyed by their cohort layer so
 * their per-cohort composition remains visible.
 */
export const getStackSegmentColorKey = (
  layerLabel: string,
  category: string,
  subgroups: string[]
): string =>
  subgroups.length === 1 && isNoValueStateLabel(category)
    ? category
    : layerLabel;

/**
 * Colour for one stacked-bar segment - see `getStackSegmentColorKey`.
 */
export const getStackSegmentColor = (
  layerLabel: string,
  category: string,
  subgroups: string[]
): string => {
  const key = getStackSegmentColorKey(
    layerLabel,
    category,
    subgroups
  );
  return getCohortGroupColor(key, subgroups.indexOf(key));
};

/**
 * The set of gradient targets a stacked chart needs: every cohort layer plus
 * the two no-value categories (so their reserved grey gradient exists for the
 * single-group case). De-duplicated, preserving the layers' original order so
 * their indexed colours stay stable.
 */
export const getStackGradientTargets = (
  subgroups: string[]
): string[] =>
  Array.from(
    new Set([
      ...subgroups,
      NOT_LOOKED_FOR_LABEL,
      LOOKED_FOR_NO_VALUE_LABEL,
    ])
  );
